import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Fade, Accordion, AccordionSummary, AccordionDetails, Box, Skeleton } from '@mui/material';
import { Add as AddIcon, Notifications as NotificationsIcon, ExpandMore as ExpandMoreIcon, PlayArrow as PlayArrowIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Navbar from '../../shared/Navbar';
import AlertRuleCard from '../components/AlertRuleCard';
import AlertRuleModal from '../components/AlertRuleModal';
import CommonDeleteConfirmDialog from '../../shared/CommonDeleteConfirmDialog';
import { fetchAlerts, createAlert, updateAlert, deleteAlert, optimisticToggleAlert, triggerAlerts } from '../../../store/alertSlice';
import { fetchAIPlanDropdown } from '../../../store/aiDevSlice';
import { COLORS } from '../../../shared/colors';
import { ICONS } from '../../../shared/icons';
import './ProjectAlerts.css';

const PROJECT_ALERT_COLOR_VARS = {
  '--project-alert-project-bg': COLORS.projectAlerts.projectHeaderBg,
  '--project-alert-accordion-header-bg': COLORS.projectAlerts.accordionHeaderBg,
  '--project-alert-panel-border': COLORS.brand.primaryDark,
  '--project-alert-card-bg': COLORS.white,
  '--project-alert-muted-border': COLORS.slate[400],
  '--project-alert-card-divider': COLORS.ui.border
};

const ProjectAlerts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectDropdownsByUser: projectsData } = useSelector((state) => state.tickets);
  const { alerts: alertsData, loading, createLoading, triggerLoading } = useSelector((state) => state.alerts);
  const { dropdownData, conditionTypes } = useSelector((state) => state.aiDev);
  
  // Get available projects
  const availableProjects = useMemo(() => {
    if (!projectsData) return [];
    if (Array.isArray(projectsData)) return projectsData;
    if (typeof projectsData === 'object') {
      const values = Object.values(projectsData);
      if (values.length > 0 && Array.isArray(values[0])) {
        return values[0];
      }
      return values;
    }
    return [];
  }, [projectsData]);

  // Convert API response to component format
  const alerts = useMemo(() => {
    if (!alertsData || typeof alertsData !== 'object') return [];
    
    const convertedAlerts = [];
    Object.entries(alertsData).forEach(([projectName, projectAlerts]) => {
      if (Array.isArray(projectAlerts)) {
        projectAlerts.forEach((alert, index) => {
          convertedAlerts.push({
            id: alert.id,
            projectId: alert.project_id,
            projectName: projectName,
            name: alert.alert_name,
            condition: {
              type: alert.condition_type,
              threshold: alert.threshold
            },
            actions: alert.action_type.split(',').map(type => ({
              type: type.trim().replace('sclack', 'slack'),
              recipients: alert.recipients
            })),
            enabled: alert.status === 'active'
          });
        });
      }
    });
    return convertedAlerts;
  }, [alertsData]);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);

  // Fetch alerts and condition types on component mount
  useEffect(() => {
    dispatch(fetchAlerts());
    if (availableProjects.length > 0) {
      const projectId = availableProjects[0].id || availableProjects[0]._id || availableProjects[0].project_id;
      dispatch(fetchAIPlanDropdown({ projectId, type: 'condition_type' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Group alerts by project
  const groupedAlerts = useMemo(() => {
    const groups = {};
    alerts.forEach(alert => {
      const projectId = alert.projectId;
      if (!groups[projectId]) {
        const project = availableProjects.find(p => (p.id || p._id || p.project_id) === projectId);
        groups[projectId] = {
          projectId,
          projectName: project?.name || alert.projectName || `Project ${projectId}`,
          alerts: []
        };
      }
      groups[projectId].alerts.push(alert);
    });
    return groups;
  }, [alerts, availableProjects]);

  const handleCreateNew = async (alertData) => {
    try {
      const payload = {
        alert_name: alertData.name,
        project_id: alertData.projectId,
        condition_type: alertData.condition?.type,
        threshold: alertData.condition?.threshold,
        action_types: alertData.actions?.map(action => action.type) || [],
        recipients: alertData.actions?.find(action => action.type === 'email')?.recipients || []
      };
      
      await dispatch(createAlert(payload)).unwrap();
      toast.success(t('projectAlerts.messages.created'));
      setModalOpen(false);
      dispatch(fetchAlerts());
    } catch (error) {
      toast.error(t(error?.message || 'projectAlerts.errors.createFailed'));
    }
  };

  const handleEdit = async (alertData) => {
    try {
      const payload = {
        alert_name: alertData.name,
        project_id: alertData.projectId,
        condition_type: alertData.condition?.type,
        threshold: alertData.condition?.threshold,
        action_types: alertData.actions?.map(action => action.type) || [],
        recipients: alertData.actions?.find(action => action.type === 'email')?.recipients || []
      };
      
      await dispatch(updateAlert({ alertId: alertData.id, alertData: payload })).unwrap();
      toast.success(t('projectAlerts.messages.updated'));
      setModalOpen(false);
      setEditingAlert(null);
      dispatch(fetchAlerts());
    } catch (error) {
      toast.error(t(error?.message || 'projectAlerts.errors.updateFailed'));
    }
  };

  const handleEditClick = (alert) => {
    setEditingAlert(alert);
    setModalOpen(true);
  };

  const handleDeleteClick = (alertId) => {
    const alert = alerts.find(a => a.id === alertId);
    setAlertToDelete(alert);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (alertToDelete) {
      try {
        await dispatch(deleteAlert(alertToDelete.id)).unwrap();
        setDeleteDialogOpen(false);
        setAlertToDelete(null);
        toast.success(t('projectAlerts.messages.deleted'));
        dispatch(fetchAlerts());
      } catch (error) {
        toast.error(t(error?.message || 'projectAlerts.errors.deleteFailed'));
      }
    }
  };

  const handleToggle = async (alertId, enabled) => {
    // Optimistically update UI immediately
    dispatch(optimisticToggleAlert({ alertId, enabled }));
    toast.success(t(enabled ? 'projectAlerts.messages.enabled' : 'projectAlerts.messages.disabled'));
    
    try {
      const alert = alerts.find(a => a.id === alertId);
      if (!alert) return;
      
      const conditionTypeObj = (conditionTypes || dropdownData?.data?.condition_type || []).find(
        ct => ct.key === alert.condition?.type
      );
      
      const payload = {
        alert_name: alert.name,
        project_id: alert.projectId,
        condition_type: conditionTypeObj?.value || alert.condition?.type,
        threshold: alert.condition?.threshold,
        action_types: alert.actions?.map(action => action.type) || [],
        recipients: alert.actions?.find(action => action.type === 'email')?.recipients || [],
        status: enabled ? 'active' : 'inactive'
      };
      
      await dispatch(updateAlert({ alertId, alertData: payload })).unwrap();
    } catch (error) {
      // Revert optimistic update on error
      dispatch(optimisticToggleAlert({ alertId, enabled: !enabled }));
      toast.error(t(error?.message || 'projectAlerts.errors.toggleFailed'));
    }
  };

  const handleCreateClick = () => {
    setEditingAlert(null);
    setModalOpen(true);
  };

  const handleTriggerAlerts = async () => {
    try {
      await dispatch(triggerAlerts()).unwrap();
      toast.success(t('projectAlerts.messages.triggerAlertsToImplement'));
    } catch (error) {
      toast.error(t(error?.message || 'projectAlerts.errors.triggerFailed'));
    }
  };

  const renderAlertSkeletons = () => (
    <div className="project-alert-skeleton-list" aria-hidden="true">
      {[0, 1, 2].map((groupIndex) => (
        <div className="project-alert-skeleton-group" key={`alert-skeleton-group-${groupIndex}`}>
          <div className="project-alert-skeleton-summary">
            <Skeleton variant="circular" width={18} height={18} animation="wave" />
            <Skeleton variant="text" width={groupIndex === 0 ? 220 : 170} height={24} animation="wave" />
          </div>
          <div className="project-alert-skeleton-card">
            <div className="project-alert-skeleton-card-header">
              <div className="project-alert-skeleton-title">
                <Skeleton variant="text" width="46%" height={30} animation="wave" />
                <Skeleton variant="rounded" width={72} height={24} animation="wave" />
              </div>
              <div className="project-alert-skeleton-actions">
                <Skeleton variant="rounded" width={88} height={24} animation="wave" />
                <Skeleton variant="circular" width={32} height={32} animation="wave" />
                <Skeleton variant="circular" width={32} height={32} animation="wave" />
              </div>
            </div>
            <div className="project-alert-skeleton-details">
              <Skeleton variant="text" width="36%" height={24} animation="wave" />
              <Skeleton variant="text" width="28%" height={24} animation="wave" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="dashboard">
      <Navbar />
      
      <main className="main project-alerts" style={PROJECT_ALERT_COLOR_VARS}>
        <Fade in timeout={600}>
          <div>
            <div className="alerts-header">
              <button type="button" onClick={() => navigate('/settings')} className="alerts-header-link">
                {t('projectAlerts.breadcrumb.settings')}
              </button>
              <NavigateNextIcon fontSize="small" className="alerts-header-separator" style={{ margin: '0 -2px' }} />
              <Typography component="span" className="alerts-header-current">
                {t('projectAlerts.breadcrumb.projectAlerts')}
              </Typography>
              <Box
                component="img"
                src={ICONS.projectAlert}
                alt=""
                aria-hidden="true"
                className="alerts-header-bell"
              />
            </div>

            <div className="alerts-content">
              <div className="alerts-content-header">
                <div>
                  <Typography variant="h5" className="alerts-content-title">
                    {t('projectAlerts.title')}
                  </Typography>
                  <Typography variant="body1" className="alerts-content-subtitle">
                    {t('projectAlerts.description')}
                  </Typography>
                </div>

                <Box className="alerts-header-actions">
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleTriggerAlerts}
                    className="trigger-alerts-button"
                    disabled={triggerLoading}
                  >
                    {triggerLoading ? t('common.processing') : t('projectAlerts.actions.triggerAlerts')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                    className="create-alert-button"
                    sx={{ bgcolor: COLORS.brand.primaryDark, color: COLORS.white, boxShadow: 'none', '&:hover': { bgcolor: COLORS.brand.primaryDarkHover, boxShadow: 'none' } }}
                  >
                    {t('projectAlerts.actions.createAlertRule')}
                  </Button>
                </Box>
              </div>

              {loading ? (
                renderAlertSkeletons()
              ) : Object.keys(groupedAlerts).length > 0 ? (
                Object.values(groupedAlerts).map((group) => (
                  group.alerts.length > 0 && (
                    <Accordion 
                      key={group.projectId} 
                      defaultExpanded 
                      className="project-alert-accordion"
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        className="project-alert-accordion-summary"
                      >
                        <Typography variant="h6" fontWeight="500" className="project-alert-title">
                          <NotificationsIcon className="project-alert-icon" />
                          {group.projectName}
                          <Box className="project-alert-count">
                            {group.alerts.length}
                          </Box>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="project-alert-accordion-details">
                        <div className="alerts-list">
                          {group.alerts.map((alert) => (
                            <AlertRuleCard
                              key={alert.id}
                              alert={alert}
                              onEdit={handleEditClick}
                              onDelete={handleDeleteClick}
                              onToggle={handleToggle}
                            />
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))
              ) : (
                <div className="empty-state">
                  <NotificationsIcon className="empty-state-icon" />
                  <Typography variant="h6" color="text.secondary" className="empty-state-title">
                    {t('projectAlerts.emptyState.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="empty-state-subtitle">
                    {t('projectAlerts.emptyState.description')}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </Fade>

        <AlertRuleModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingAlert(null);
          }}
          onSave={editingAlert ? handleEdit : handleCreateNew}
          alert={editingAlert}
          availableProjects={availableProjects}
          conditionTypes={dropdownData?.data?.condition_type || []}
          loading={createLoading}
        />

        <CommonDeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title={t('projectAlerts.deleteDialog.title')}
          question={t('projectAlerts.deleteDialog.question', 'Are you sure you want to delete the alert rule?')}
          itemName={alertToDelete?.name || ''}
          warning={t('projectAlerts.deleteDialog.warning', 'This action cannot be undone.')}
          confirmText={t('projectAlerts.deleteDialog.confirm')}
          cancelText={t('projectAlerts.deleteDialog.cancel')}
        />
      </main>
    </div>
  );
};

export default ProjectAlerts;
